import React, {
  useContext, useEffect, useRef, useState,
} from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { faPencil } from '@fortawesome/pro-solid-svg-icons'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { faUndo } from '@fortawesome/pro-solid-svg-icons'

import GlobalContext from 'agency/digital_renderer/utils/context'
import deviceStyle from 'agency/digital_renderer/utils/deviceStyle'
import replaceTextShortCodes, { validateRenderConditions } from 'functions/replaceTextShortCodes'

import AnimationWrapper from 'agency/digital_renderer/themes/RealhubDynamic/ui/AnimationWrapper'
import styles from './styles.scss'

const replaceCustomData = (componentId, { customData }) => {
  if (componentId && customData){
    const itemData = customData[componentId] || {}
    return itemData.text || ''
  }

  return ''
}

const TextBlock = (props) => {
  const textContainer = useRef(null)
  const input = useRef(null)

  const {
    callbacks: { updateCustomData },
    context: componentContext,
    data: { shortcode, value: rawValue },
    id,
    options,
    options: { editable, renderConditions },
  } = props

  // Context
  const defaultContext = useContext(GlobalContext)
  const { editing } = defaultContext
  const contextData = { ...defaultContext, ...componentContext }

  // Text Block Value
  const value = replaceCustomData(id, defaultContext) || replaceTextShortCodes(rawValue, contextData)

  // State
  const [inputValue, setInputValue] = useState(value)
  const [editMode, setEditMode] = useState(false)
  const [inputHeight, setInputHeight] = useState(null)

  // Effects
  useEffect(() => {
    if (inputValue !== value){
      setInputValue(value)
    }
  }, [value])

  // Should Render
  const shouldRender = validateRenderConditions(renderConditions, contextData)
  if (!shouldRender) return null

  // Style
  const style = deviceStyle(props)

  // Functions
  const toggleEditMode = (e) => {
    e.stopPropagation()

    if (textContainer.current){
      setInputHeight(textContainer.current.clientHeight)
    }

    setEditMode(true)
  }

  const saveEdit = (e) => {
    e.stopPropagation()

    if (updateCustomData){
      updateCustomData(id, {
        shortcode,
        text: inputValue,
      })
    }

    setEditMode(false)
  }

  const cancelEdit = (e) => {
    e.stopPropagation()

    setInputValue(value)
    setEditMode(false)
  }

  const resetEdit = (e) => {
    e.stopPropagation()

    if (updateCustomData){
      updateCustomData(id, {
        shortcode,
        text: null,
      })

      setEditMode(false)
    }
  }

  const isEditable = editing && editable && updateCustomData

  return (
    <AnimationWrapper options={options}>
      {!editMode ? (
        <div
          className={classnames(styles.root, isEditable ? styles.editable : null)}
          onClick={isEditable ? e => toggleEditMode(e) : null}
          ref={textContainer}
          style={style}
        >
          {value}
          {isEditable && (
            <div className={styles.editActions}>
              <div className={styles.icon} style={{ marginBottom: 2 }}>
                <FontAwesomeIcon icon={faPencil} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.editContainer}>
          <textarea
            className={styles.editInput}
            onClick={e => e.stopPropagation()}
            onChange={e => setInputValue(e.target.value)}
            ref={input}
            style={{ ...style, height: inputHeight }}
            value={inputValue}
          />
          <div className={styles.editActions}>
            <a
              className={classnames(styles.icon, styles.blue)}
              onClick={e => resetEdit(e)}
              style={{ marginRight: 2 }}
              tabIndex="0"
            >
              <FontAwesomeIcon icon={faUndo} />
            </a>

            <a
              className={classnames(styles.icon, styles.green)}
              onClick={e => saveEdit(e)}
              style={{ marginRight: 2 }}
              tabIndex="0"
            >
              <FontAwesomeIcon icon={faCheck} />
            </a>

            <a className={classnames(styles.icon, styles.red)} onClick={e => cancelEdit(e)} tabIndex="0">
              <FontAwesomeIcon icon={faTimes} />
            </a>
          </div>
        </div>
      )}
    </AnimationWrapper>
  )
}

TextBlock.propTypes = {
  callbacks: PropTypes.shape({
    updateCustomData: PropTypes.func,
  }),
  context: PropTypes.object,
  data: PropTypes.object,
  id: PropTypes.string.isRequired,
  options: PropTypes.object,
}

TextBlock.defaultProps = {
  data: {},
  options: {},
}

export default TextBlock
